import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause } from '@fortawesome/free-solid-svg-icons';

const BAR_COUNT = 40;
const SPEED_OPTIONS = [1, 1.5, 2];

function formatDuration(secs: number): string {
  if (!secs || !isFinite(secs) || secs < 0) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function computeWaveformBars(audioBuffer: AudioBuffer, barCount: number = BAR_COUNT): number[] {
  const rawData = audioBuffer.getChannelData(0);
  const samplesPerBar = Math.floor(rawData.length / barCount);
  if (samplesPerBar === 0) return [];

  const bars: number[] = [];
  for (let i = 0; i < barCount; i++) {
    let sum = 0;
    const start = i * samplesPerBar;
    for (let j = start; j < start + samplesPerBar && j < rawData.length; j++) {
      sum += Math.abs(rawData[j]);
    }
    bars.push(sum / samplesPerBar);
  }

  const max = Math.max(...bars, 0.01);
  return bars.map((b) => b / max);
}

interface VoiceMessagePlayerProps {
  audioBlob: Blob;
  durationSecs?: number;
  mimeType: string;
  isSentByMe: boolean;
}

const VoiceMessagePlayer: React.FC<VoiceMessagePlayerProps> = ({
  audioBlob,
  durationSecs,
  mimeType,
  isSentByMe,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(durationSecs || 0);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  // Decoded audio buffer (not tied to any AudioContext)
  const audioBufferRef = useRef<AudioBuffer | null>(null);

  // Playback context — created lazily on first play (user gesture)
  const playbackCtxRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const playStartCtxTimeRef = useRef(0);
  const playOffsetRef = useRef(0);
  const playbackRateRef = useRef(1);
  const animFrameRef = useRef<number>(0);
  const waveformRef = useRef<HTMLDivElement>(null);

  // Decode audio on mount using a temporary AudioContext (closed immediately after)
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const arrayBuffer = await audioBlob.arrayBuffer();
        if (cancelled) return;

        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        const tempCtx = new AudioCtx();
        const decoded = await tempCtx.decodeAudioData(arrayBuffer);
        tempCtx.close();

        if (cancelled) return;

        audioBufferRef.current = decoded;
        setTotalDuration((prev) => (prev > 0 ? prev : decoded.duration));
        setWaveformData(computeWaveformBars(decoded));
        setIsReady(true);
      } catch (err) {
        console.error('Failed to decode voice message audio:', err);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [audioBlob]);

  // Cleanup playback context and animation on unmount
  useEffect(() => {
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      if (sourceNodeRef.current) {
        try { sourceNodeRef.current.stop(); } catch {}
        sourceNodeRef.current = null;
      }
      if (playbackCtxRef.current) {
        playbackCtxRef.current.close().catch(() => {});
        playbackCtxRef.current = null;
      }
    };
  }, []);

  // Animate currentTime while playing (accounts for playbackRate)
  useEffect(() => {
    if (!isPlaying) return;

    const tick = () => {
      const ctx = playbackCtxRef.current;
      if (ctx) {
        const realElapsed = ctx.currentTime - playStartCtxTimeRef.current;
        const audioElapsed = realElapsed * playbackRateRef.current + playOffsetRef.current;
        setCurrentTime(audioElapsed);
      }
      animFrameRef.current = requestAnimationFrame(tick);
    };

    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isPlaying]);

  // Helper: ensure playback AudioContext exists and is running
  const ensurePlaybackCtx = useCallback(async (): Promise<AudioContext> => {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!playbackCtxRef.current || playbackCtxRef.current.state === 'closed') {
      playbackCtxRef.current = new AudioCtx();
    }
    const ctx = playbackCtxRef.current;
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    if (!gainNodeRef.current || gainNodeRef.current.context !== ctx) {
      const gain = ctx.createGain();
      gain.gain.value = 1.0;
      gain.connect(ctx.destination);
      gainNodeRef.current = gain;
    }
    return ctx;
  }, []);

  // Helper: start playback from a given offset with current rate
  const startPlaybackFrom = useCallback((ctx: AudioContext, buffer: AudioBuffer, offset: number, rate: number) => {
    // Stop any existing source
    if (sourceNodeRef.current) {
      try { sourceNodeRef.current.onended = null; sourceNodeRef.current.stop(); } catch {}
      sourceNodeRef.current = null;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = rate;
    source.connect(gainNodeRef.current!);

    source.onended = () => {
      if (sourceNodeRef.current === source) {
        setIsPlaying(false);
        setCurrentTime(0);
        playOffsetRef.current = 0;
        sourceNodeRef.current = null;
      }
    };

    playStartCtxTimeRef.current = ctx.currentTime;
    playOffsetRef.current = offset;
    source.start(0, offset);
    sourceNodeRef.current = source;
  }, []);

  // Helper: get current audio position
  const getCurrentPosition = useCallback((): number => {
    const ctx = playbackCtxRef.current;
    const buffer = audioBufferRef.current;
    if (!ctx || !buffer) return playOffsetRef.current;
    const realElapsed = ctx.currentTime - playStartCtxTimeRef.current;
    return Math.min(realElapsed * playbackRateRef.current + playOffsetRef.current, buffer.duration);
  }, []);

  const togglePlayPause = useCallback(async () => {
    const buffer = audioBufferRef.current;
    if (!buffer) return;

    if (isPlaying) {
      // Pause: save position, stop source
      playOffsetRef.current = getCurrentPosition();
      if (sourceNodeRef.current) {
        try { sourceNodeRef.current.onended = null; sourceNodeRef.current.stop(); } catch {}
        sourceNodeRef.current = null;
      }
      setIsPlaying(false);
      return;
    }

    const ctx = await ensurePlaybackCtx();

    // If at the end, restart from beginning
    if (playOffsetRef.current >= buffer.duration) {
      playOffsetRef.current = 0;
      setCurrentTime(0);
    }

    startPlaybackFrom(ctx, buffer, playOffsetRef.current, playbackRateRef.current);
    setIsPlaying(true);
  }, [isPlaying, ensurePlaybackCtx, startPlaybackFrom, getCurrentPosition]);

  // Seek: click on waveform to jump to position
  const handleWaveformClick = useCallback(async (e: React.MouseEvent<HTMLDivElement>) => {
    const buffer = audioBufferRef.current;
    const container = waveformRef.current;
    if (!buffer || !container) return;

    const rect = container.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const seekTime = fraction * buffer.duration;

    if (isPlaying) {
      // Seek while playing: restart from new position
      const ctx = await ensurePlaybackCtx();
      startPlaybackFrom(ctx, buffer, seekTime, playbackRateRef.current);
    } else {
      // Seek while paused: just update offset
      playOffsetRef.current = seekTime;
    }
    setCurrentTime(seekTime);
  }, [isPlaying, ensurePlaybackCtx, startPlaybackFrom]);

  // Speed toggle: cycle through 1x → 1.5x → 2x
  const handleSpeedToggle = useCallback(async () => {
    const currentIdx = SPEED_OPTIONS.indexOf(playbackRateRef.current);
    const nextIdx = (currentIdx + 1) % SPEED_OPTIONS.length;
    const newRate = SPEED_OPTIONS[nextIdx];

    playbackRateRef.current = newRate;
    setPlaybackRate(newRate);

    if (isPlaying) {
      const buffer = audioBufferRef.current;
      const ctx = playbackCtxRef.current;
      if (!buffer || !ctx) return;

      // Save current position, restart with new rate
      const pos = getCurrentPosition();
      startPlaybackFrom(ctx, buffer, pos, newRate);
    }
  }, [isPlaying, getCurrentPosition, startPlaybackFrom]);

  const effectiveDuration = totalDuration > 0 ? totalDuration : 0;
  const progress = effectiveDuration > 0 ? Math.min(currentTime / effectiveDuration, 1) : 0;

  const bars = waveformData.length > 0
    ? waveformData.slice(0, BAR_COUNT)
    : Array.from({ length: BAR_COUNT }, () => 0.4);

  return (
    <div className={`voice-player ${isSentByMe ? 'voice-player--sent' : 'voice-player--received'}`}>
      <button className="voice-player__play-btn" onClick={togglePlayPause} disabled={!isReady}>
        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
      </button>

      <div className="voice-player__waveform" ref={waveformRef} onClick={handleWaveformClick}>
        {bars.map((amplitude, i) => {
          const barProgress = i / BAR_COUNT;
          const isBarPlayed = barProgress <= progress;
          return (
            <div
              key={i}
              className={`voice-player__bar ${isBarPlayed ? 'voice-player__bar--played' : ''}`}
              style={{ height: `${Math.max(amplitude * 100, 8)}%` }}
            />
          );
        })}
      </div>

      <div className="voice-player__controls">
        <span className="voice-player__duration">
          {formatDuration(isPlaying ? currentTime : (totalDuration || (durationSecs || 0)))}
        </span>
        {playbackRate !== 1 || isPlaying ? (
          <button className="voice-player__speed-btn" onClick={handleSpeedToggle}>
            {playbackRate}x
          </button>
        ) : (
          <button className="voice-player__speed-btn" onClick={handleSpeedToggle}>
            1x
          </button>
        )}
      </div>
    </div>
  );
};

export default VoiceMessagePlayer;
